/**
 * LA 2028 City/Zone Template JavaScript
 * Handles interactive map, venue filtering, and dynamic content
 */

(function() {
  'use strict';

  // Zone color mappings
  const ZONE_COLORS = {
    'downtown-la': '#6366f1',
    'westside-beach': '#3b82f6',
    'westside-and-beach': '#3b82f6',
    'south-bay': '#06b6d4',
    'inglewood': '#ec4899',
    'pasadena': '#ef4444',
    'san-fernando-valley': '#10b981',
    'orange-county': '#f59e0b',
    'inland-other': '#8b5cf6',
    'inland-and-other': '#8b5cf6'
  };

  // Zone transportation info
  const ZONE_TRANSPORT = {
    'downtown-la': {
      metro: ['B Line (Red)', 'D Line (Purple)', 'E Line (Expo)', 'A Line (Blue)', 'L Line (Gold)'],
      description: 'Downtown LA is the hub of the Metro system with multiple lines converging. All venues are Metro-accessible.',
      parking: 'Limited street parking. Use Metro or rideshare recommended.',
      tips: 'Metro is the fastest option. Purchase TAP card for unlimited rides.'
    },
    'westside-and-beach': {
      metro: ['E Line (Expo) to Venice + Shuttle'],
      description: 'E Line to Downtown Santa Monica, then shuttle to Venice Beach venues.',
      parking: 'Beach parking lots available but fill early. Arrive before 9am.',
      tips: 'Consider biking the Marvin Braude Bike Trail from Santa Monica Pier.'
    },
    'south-bay': {
      metro: ['C Line (Green)', 'A Line (Blue)'],
      description: 'C Line serves Carson venues. A Line connects to Long Beach.',
      parking: 'Ample parking at Carson complex. Long Beach has paid lots.',
      tips: 'Carson venues are clustered - easy to walk between them.'
    },
    'inglewood': {
      metro: ['K Line (Crenshaw)', 'C Line (Green)'],
      description: 'New K Line opened 2022 with direct access to SoFi Stadium.',
      parking: 'Pre-paid parking only at SoFi. Book in advance.',
      tips: 'K Line is brand new and connects to LAX. Perfect for visitors.'
    },
    'pasadena': {
      metro: ['L Line (Gold) + Shuttle'],
      description: 'L Line to Memorial Park station, then free shuttle to Rose Bowl.',
      parking: 'Rose Bowl has 12,000+ parking spaces. Arrive 2+ hours early.',
      tips: 'Shuttle runs every 10 minutes on event days.'
    },
    'san-fernando-valley': {
      metro: ['G Line (Orange - BRT)'],
      description: 'G Line Bus Rapid Transit serves Valley Complex.',
      parking: 'Free parking at Valley Sports Complex.',
      tips: 'Consider staying in Studio City or North Hollywood for easy access.'
    },
    'orange-county': {
      metro: ['Metrolink Orange County Line'],
      description: 'Metrolink commuter rail from Union Station to Anaheim.',
      parking: 'Ample parking at Honda Center and Trestles Beach.',
      tips: 'Rent a car if visiting multiple OC venues. Metrolink for Anaheim only.'
    },
    'inland-and-other': {
      metro: ['Metrolink Inland Empire Line'],
      description: 'Metrolink required for most inland venues. Limited Metro access.',
      parking: 'Free parking at most inland venues.',
      tips: 'Rental car recommended. Venues are spread across 50+ miles.'
    }
  };

  // Initialize when DOM is ready
  function init() {
    loadVenueData();
    setupSortControls();
    populateTransportInfo();
    setupSmoothScrolling();
  }

  // Load venue data from JSON
  function loadVenueData() {
    const dataScript = document.getElementById('la2028-venue-data');
    if (!dataScript) {
      console.warn('Venue data not found');
      return;
    }

    try {
      const venueData = JSON.parse(dataScript.textContent);
      window.LA2028_VENUES = venueData.venues || [];
      window.LA2028_ZONES = venueData.zones || {};

      // Store current zone's venues
      const currentZone = window.LA2028_ZONE || '';
      window.LA2028_ZONE_VENUES = window.LA2028_VENUES.filter(v => v.zone === currentZone);

      console.log(`Loaded ${window.LA2028_ZONE_VENUES.length} venues for zone: ${currentZone}`);
    } catch (e) {
      console.error('Failed to parse venue data:', e);
    }
  }

  // Setup product sorting controls
  function setupSortControls() {
    const sortButtons = document.querySelectorAll('.map-control-btn[data-sort]');
    const container = document.getElementById('products-container');

    if (!container || sortButtons.length === 0) return;

    sortButtons.forEach(button => {
      button.addEventListener('click', function() {
        // Update active state
        sortButtons.forEach(btn => btn.classList.remove('active'));
        this.classList.add('active');

        const sortType = this.getAttribute('data-sort');
        sortProducts(sortType, container);
      });
    });
  }

  // Sort products by venue, sport, or date
  function sortProducts(sortType, container) {
    const venueGroups = container.querySelector('.venue-groups');
    if (!venueGroups) return;

    const productCards = Array.from(venueGroups.querySelectorAll('.product-card'));
    const venueGroupElements = Array.from(venueGroups.querySelectorAll('.venue-group'));

    switch (sortType) {
      case 'sport':
        sortBySport(productCards, container);
        break;
      case 'date':
        sortByDate(productCards, container);
        break;
      case 'venue':
      default:
        // Restore original venue grouping
        venueGroupElements.forEach(group => {
          group.style.display = '';
        });
        break;
    }
  }

  // Sort products by sport
  function sortBySport(productCards, container) {
    // Group by sport
    const sportGroups = {};

    productCards.forEach(card => {
      const sportBadge = card.querySelector('.product-sport-badge');
      const sport = sportBadge ? sportBadge.textContent.trim() : 'Other';

      if (!sportGroups[sport]) {
        sportGroups[sport] = [];
      }
      sportGroups[sport].push(card.cloneNode(true));
    });

    // Hide original venue groups
    const venueGroups = container.querySelectorAll('.venue-group');
    venueGroups.forEach(group => {
      group.style.display = 'none';
    });

    // Create sport-based groups
    let sportHTML = '<div class="venue-groups" id="sport-sorted-groups">';

    Object.keys(sportGroups).sort().forEach(sport => {
      const cards = sportGroups[sport];
      const sportSlug = sport.toLowerCase().replace(/[^a-z0-9]+/g, '-');

      sportHTML += `
        <article class="venue-group" id="sport-${sportSlug}">
          <div class="venue-group-header">
            <div>
              <h3 class="venue-group-title">${sport}</h3>
              <div class="venue-meta">
                <span class="venue-meta-item">
                  <span>🎫</span>
                  <span>${cards.length} ${cards.length === 1 ? 'Event' : 'Events'}</span>
                </span>
              </div>
            </div>
            <a href="/collections/summer-olympics-${sportSlug}" class="view-venue-link">
              View All ${sport} →
            </a>
          </div>
          <div class="product-grid">
            ${cards.map(card => card.outerHTML).join('')}
          </div>
        </article>
      `;
    });

    sportHTML += '</div>';

    // Replace content
    const existingSportGroups = document.getElementById('sport-sorted-groups');
    if (existingSportGroups) {
      existingSportGroups.remove();
    }

    container.insertAdjacentHTML('beforeend', sportHTML);
  }

  // Sort products by date
  function sortByDate(productCards, container) {
    // Group by date
    const dateGroups = {};

    productCards.forEach(card => {
      const dateElement = card.querySelector('.product-meta-item:has(span:first-child)');
      let dateText = 'TBA';

      if (dateElement) {
        const spans = dateElement.querySelectorAll('span');
        if (spans.length > 1) {
          dateText = spans[1].textContent.trim();
        }
      }

      if (!dateGroups[dateText]) {
        dateGroups[dateText] = [];
      }
      dateGroups[dateText].push(card.cloneNode(true));
    });

    // Hide original venue groups
    const venueGroups = container.querySelectorAll('.venue-group');
    venueGroups.forEach(group => {
      group.style.display = 'none';
    });

    // Create date-based groups
    let dateHTML = '<div class="venue-groups" id="date-sorted-groups">';

    Object.keys(dateGroups).sort().forEach(date => {
      const cards = dateGroups[date];
      const dateSlug = date.toLowerCase().replace(/[^a-z0-9]+/g, '-');

      dateHTML += `
        <article class="venue-group" id="date-${dateSlug}">
          <div class="venue-group-header">
            <div>
              <h3 class="venue-group-title">${date}</h3>
              <div class="venue-meta">
                <span class="venue-meta-item">
                  <span>🎫</span>
                  <span>${cards.length} ${cards.length === 1 ? 'Event' : 'Events'}</span>
                </span>
              </div>
            </div>
          </div>
          <div class="product-grid">
            ${cards.map(card => card.outerHTML).join('')}
          </div>
        </article>
      `;
    });

    dateHTML += '</div>';

    // Replace content
    const existingDateGroups = document.getElementById('date-sorted-groups');
    if (existingDateGroups) {
      existingDateGroups.remove();
    }

    container.insertAdjacentHTML('beforeend', dateHTML);
  }

  // Populate transportation information
  function populateTransportInfo() {
    const transportContainer = document.getElementById('transport-info');
    if (!transportContainer) return;

    const zoneSlug = window.LA2028_ZONE_SLUG || '';
    const transportInfo = ZONE_TRANSPORT[zoneSlug];

    if (!transportInfo) {
      console.warn('No transport info for zone:', zoneSlug);
      return;
    }

    const html = `
      <div class="transport-card">
        <span class="transport-icon">🚇</span>
        <h3 class="transport-title">Metro & Rail</h3>
        <p class="transport-description">${transportInfo.description}</p>
        <div class="metro-lines">
          ${transportInfo.metro.map(line => `
            <span class="metro-line-badge">${line}</span>
          `).join('')}
        </div>
      </div>

      <div class="transport-card">
        <span class="transport-icon">🅿️</span>
        <h3 class="transport-title">Parking</h3>
        <p class="transport-description">${transportInfo.parking}</p>
      </div>

      <div class="transport-card">
        <span class="transport-icon">💡</span>
        <h3 class="transport-title">Travel Tips</h3>
        <p class="transport-description">${transportInfo.tips}</p>
      </div>
    `;

    transportContainer.innerHTML = html;
  }

  // Setup smooth scrolling for anchor links
  function setupSmoothScrolling() {
    document.addEventListener('click', function(e) {
      const target = e.target.closest('a[href^="#"]');
      if (!target) return;

      const href = target.getAttribute('href');
      if (href === '#') return;

      const element = document.querySelector(href);
      if (element) {
        e.preventDefault();
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });

        // Update URL without jumping
        if (window.history && window.history.pushState) {
          window.history.pushState(null, '', href);
        }
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Handle window resize for map
  let resizeTimer;
  window.addEventListener('resize', function() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function() {
      if (window.LA2028_ZONE_MAP) {
        window.LA2028_ZONE_MAP.invalidateSize();
      }
    }, 250);
  });

  // Mobile menu handling
  function setupMobileControls() {
    const sortControls = document.querySelector('.map-controls');
    if (!sortControls) return;

    // Make controls scrollable on mobile
    if (window.innerWidth < 768) {
      sortControls.style.overflowX = 'auto';
      sortControls.style.WebkitOverflowScrolling = 'touch';
    }
  }

  // Setup mobile controls on load and resize
  setupMobileControls();
  window.addEventListener('resize', setupMobileControls);

})();
